import { useGet, usePut } from './templateHooks';
import {
  CreateOrgInputType,
  OrgType,
  OrgSSEData,
  UpdateOrgType,
  DashboardRefreshType,
  OrgTypeWithRole,
} from 'src/@types/org';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAxios } from './useFetch';
import { FAST_API_URL, ROS_URL } from 'src/config';
import { useCurrentOrgContext } from 'src/contexts/OrgContext';
import { useSSE } from './useSSE';

export const useOrg = (orgId: string | null = null, config = {}) => {
  const { orgId: currentOrgId } = useCurrentOrgContext();

  const fetchData = useAxios<OrgType>({
    endpoint: '/v2/orgs/' + (orgId ?? currentOrgId),
    method: 'get',
    url: FAST_API_URL,
  });

  return useQuery({
    queryKey: ['org', orgId ?? currentOrgId],
    queryFn: () => fetchData({}),
    staleTime: Infinity,
    enabled: !!(orgId ?? currentOrgId),
    ...config,
  });
};

export const useApproveOrg = (orgId: string) => {
  const { orgId: currentOrgId } = useCurrentOrgContext();

  const queryClient = useQueryClient();

  const fetch = useAxios<OrgType, undefined>({
    endpoint: '/v1/orgs/' + orgId + '/approve',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: () => fetch({}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
      queryClient.invalidateQueries({ queryKey: ['orgs', 'children', currentOrgId] });
      queryClient.invalidateQueries({ queryKey: ['orgs', 'linkable', currentOrgId] });
      queryClient.invalidateQueries({ queryKey: ['campaignLeaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['campaignActivityTimestamps'] });
    },
  });
};

export const useRejectOrg = (orgId: string) => {
  const { orgId: currentOrgId } = useCurrentOrgContext();

  const queryClient = useQueryClient();

  const fetch = useAxios<undefined, undefined>({
    endpoint: '/v1/orgs/' + orgId + '/reject',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: () => fetch({}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
      queryClient.invalidateQueries({ queryKey: ['orgs', 'children', currentOrgId] });
      queryClient.invalidateQueries({ queryKey: ['orgs', 'linkable', currentOrgId] });
      queryClient.invalidateQueries({ queryKey: ['campaignLeaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['campaignActivityTimestamps'] });
    },
  });
};

export const useOrgs = (
  options: {
    include_deactivated_orgs?: boolean;
    config?: Record<string, boolean>;
  } = {}
) =>
  useGet<OrgTypeWithRole[], object>({
    queryKey: ['user', 'orgs'],
    endpoint: '/v2/my-orgs',
    config: options.config,
    params: { include_deactivated_orgs: options.include_deactivated_orgs },
    url: FAST_API_URL,
  });

export function useUpdateOrg() {
  const queryClient = useQueryClient();
  const useUpdateOrgMutation = usePut<UpdateOrgType, OrgType>(
    ['orgs'],
    '/v1/orgs',
    undefined,
    undefined,
    FAST_API_URL
  );
  const updateOrg = async (orgId: string, updatedFields: UpdateOrgType, onSuccess?: () => void) => {
    await useUpdateOrgMutation.mutateAsync(
      { data: updatedFields, resourceId: orgId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['org', orgId] });
          queryClient.invalidateQueries({ queryKey: ['monthlyBilling'] });
          if (onSuccess) {
            onSuccess();
          }
        },
      }
    );
  };
  return { updateOrg, ...useUpdateOrgMutation };
}

export const useCreateOrg = () => {
  const queryClient = useQueryClient();
  const endpoint = '/v1/orgs';
  const method = 'post';

  const createOrg = useAxios<OrgType, CreateOrgInputType>({
    endpoint: endpoint,
    method: method,
    url: FAST_API_URL,
  });

  return useMutation({
    mutationFn: createOrg,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
    },
  });
};

export const useRefreshDashboard = () => {
  const refreshDashboard = useAxios<undefined, DashboardRefreshType>({
    endpoint: '/v1/models/refresh-dashboard',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: ({ data }: { data: DashboardRefreshType }) => refreshDashboard({ data: data }),
  });
};

export function useArchiveOrg() {
  const queryClient = useQueryClient();
  const archiveOrg = useAxios<undefined, undefined>({
    endpoint: '/v1/orgs',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: ({ resourceId }: { resourceId?: string }) =>
      archiveOrg({ resourceId: resourceId + '/archive' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
    },
  });
}

export const useDeleteOrg = () => {
  const queryClient = useQueryClient();
  const deleteOrg = useAxios<undefined, undefined>({
    endpoint: '/v1/orgs',
    method: 'delete',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: ({ resourceId }: { resourceId?: string }) => deleteOrg({ resourceId: resourceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
    },
  });
};

export const useUnarchiveOrg = () => {
  const queryClient = useQueryClient();
  const unarchiveOrg = useAxios<undefined, undefined>({
    endpoint: '/v1/orgs',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn: ({ resourceId }: { resourceId?: string }) =>
      unarchiveOrg({ resourceId: resourceId + '/unarchive' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'orgs'] });
    },
  });
};

// ── useOrgSSEStatus ───────────────────────────────────────────────────────────

type OrgSSEMessage = {
  type: 'order_status' | 'unread_conversations';
  value?: string;
  cnt?: number;
};

function useOrgStatus(orgId: string | null) {
  return useGet<OrgSSEData>({
    queryKey: ['orgStatus', orgId ?? ''],
    endpoint: '/org/status',
    url: ROS_URL,
    config: { enabled: !!orgId },
  });
}

function useOrgSSE(orgId: string | null, enabled: boolean) {
  const queryClient = useQueryClient();

  useSSE<OrgSSEMessage>('events/source/org', {
    enabled: enabled && !!orgId,
    eventName: 'org_update',
    useRos: true,
    onMessage: (message) => {
      queryClient.setQueryData<OrgSSEData>(['orgStatus', orgId ?? ''], (prev) => {
        if (!prev) return prev;
        console.log('Received Org SSE message:', message);
        switch (message.type) {
          case 'order_status':
            return { ...prev, order_status: message.value ?? prev.order_status };
          case 'unread_conversations':
            queryClient.invalidateQueries({ queryKey: ['convUnreadCount'] });
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            return prev;
          default:
            return prev;
        }
      });
    },
  });
}

export function useOrgSSEStatus() {
  const { orgId } = useCurrentOrgContext();
  const { data, isLoading, error } = useOrgStatus(orgId);
  useOrgSSE(orgId, !!data);
  return { data, isLoading, error };
}

export function useAcceptDtTerms() {
  const { orgId } = useCurrentOrgContext();
  const queryClient = useQueryClient();

  return usePut<null, undefined>(
    ['accept-dt-terms'],
    '/v1/accept-dt-terms',
    { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org', orgId] }) },
    undefined,
    FAST_API_URL
  );
}
